import { ApiResponse } from '../../services/apiService';
import { React, useState, useEffect } from '../../utils/globalImports';
import { AppLayout, FilterCard, CustomAddUserModal, CustomDataTable, useApi, Toaster, validationMessages, CustomBackdrop } from '../../utils/globalImports';
import { useConfirmCloseDialog } from '../../contexts/ConfirmCloseDialogContext';

interface User {
  userId?: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  roleId: number;
  status: number;
  roleName?: string;
  createdId?: number;
  updatedId?: number;
}

const Users: React.FC = () => {

    // State to manage the modal visibility
    const [modalOpen, setModalOpen] = useState(false);
    const [userDatalist, setUserDatalist] = useState<any[]>([]);
    const [userData, setUserData] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);  // State to manage confirm dialog "open/close" state
    const [isOn, setIsOn] = useState<boolean>();  // State to manage the toggle button's "on/off" state
    const [selectedUser, setSelectedUser] = useState<any | null>(null); // Holds data of the user being edited or null for add
    const [currentStatus, setcurrentStatus] = useState<string[]>(['Active']);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    
    const { execute, loading } = useApi();
    const { showConfirmCloseDialog } = useConfirmCloseDialog();

    const props = { title: 'Users', actionText: 'Add User', searchbarText: validationMessages.user_searchText }

    const userId = Number(localStorage.getItem("userId"));

    useEffect(() => {
        fetchUsers();
    }, []);  // The empty dependency array ensures it only runs once when the component mounts

    const sortOptions = [
        { value: "name", text: "Name" },
        { value: "email", text: "Email" },
    ];

    // Fetch users
    const fetchUsers = async () => {
        const { data } = await execute<User[]>('get', '/User/GetAllUsers');
        if (data) {
            setUserDatalist(data)
            // Use reduce to exclude unwanted fields
            const userData = data
                .map((user) => {
                    const { userId, firstName, lastName, email, status, roleId } = user; // Exclude userID, password, createdId, updatedId, createdAt, updatedAt`
                    // Merge firstName and lastName into 'name'
                    const name = `${firstName} ${lastName}`;
                    // Set status to 'Active' if status is 1, otherwise 'DeActive'

                    // const firstLetterOfFirstName = firstName?.charAt(0).toUpperCase() || '';
                    // const firstLetterOfLastName = lastName?.charAt(0).toUpperCase() || '';

                    // // Combine both first letters
                    // const shortName = `${firstLetterOfFirstName}${firstLetterOfLastName}`;

                    const Status = status === 1 ? 'Active' : 'Inactive';
                    return { userId, roleId, name, email, Status };
                });
            setUserData(userData);
            // Filter for status = 1 (Active) and set to filteredData
            const activeUsers = userData
                .filter((user) => currentStatus.includes(user.Status))
                .sort((a, b) =>
                    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
                    a.email.localeCompare(b.email, undefined, { sensitivity: 'base' })
                );
            setFilteredData(activeUsers);
        }
    };

    // filter Data
    const handleSearchGridData = (query: string, status: Number) => {
        const lowerCaseQuery = query.toLowerCase();

        // Map status strings to corresponding numbers
        const statusMapping: Record<string, number> = {
            Active: 1,
            Inactive: 0,
        };

        const filtered = userData.filter((item) => {
            // Convert item.Status to its numeric equivalent or skip if it doesn't match
            const itemStatus = statusMapping[item.Status] ?? -1;

            const matchesQuery =
                item.name.toLowerCase().includes(lowerCaseQuery) ||
                item.email.toLowerCase().includes(lowerCaseQuery);

            const matchesStatus =
                status === -1|| itemStatus === status;
            return matchesQuery && matchesStatus; // Both conditions must be true
        })
            .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
                a.email.localeCompare(b.email, undefined, { sensitivity: 'base' })
            );

        setFilteredData(filtered);
        setcurrentStatus(
            status === 0
                ? ['Inactive'] // When status is 0, set currentStatus to 'Inactive'
                : status === -1
                    ? ['Active', 'Inactive'] // When status is -1 or 2, set currentStatus to both 'Active' and 'Inactive'
                    : status === 1
                        ? ['Active'] // When status is 1, set currentStatus to 'Active'
                        : currentStatus // Fallback to the current value if no conditions are met
        );
    }

    // Handle form submission (add or edit user)
    const handleFormSubmit = async (formData: any) => {

        let response: ApiResponse<any>;
        if (selectedUser) {
            const updatedUser = userDatalist.find((user) => user.userId === selectedUser.userId);
            // If the user is found, update the fields
            if (updatedUser) {
                // Step 2: Update the fields on the filtered record
                const updatedUserData = {
                    ...updatedUser, // Keep the original user data
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    Password: '',
                    email: formData.email,
                    roleId: formData.roleId,
                    status: formData.Status === 'Active' ? 1 : 0, // Update based on Status
                    updatedId: userId,
                };

                response = await execute('put', '/User/UpdateUser', updatedUserData);
            }
        } else {

            // Adding a new user
            const user: User = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                password: '',
                roleId: formData.roleId,
                status: formData.Status === 'Active' ? 1 : 0,
                createdId: userId,
                roleName: ''
            };

            response = await execute('post', '/User/AddUser', user);
        }

        if (response!) {
            if (response.error) {
                // If there's an error in the response, display the error message
                setToast({ open: true, message: response.error, type: "error" });
            } else {
                // If the response is successful, display the success message
                setToast({ open: true, message: response.data, type: "success" });
                handleCloseModal(false);
                fetchUsers();
            }
        }

    };

    const handleEdit = (user: any) => {
        setSelectedUser(user); // Set the user to edit
        handleOpenModal(); // Open the modal
    };

    // Open modal
    const handleOpenModal = () => {
        setModalOpen(true);
    };

    // Close modal
  const handleCloseModal = (closeConfirmationRequired: boolean = true) => {
    if (hasUnsavedChanges && closeConfirmationRequired) {
       showConfirmCloseDialog({
          onConfirm: () => { 
            setHasUnsavedChanges(false);  
            setModalOpen(false); 
            setSelectedUser(null);      
          },
          onCancel: () => { 
          },
        });
    } else {
      setModalOpen(false);
      setSelectedUser(null); // Reset selectedUser when modal closes
    }
  };

    const handleToggleState = (row: any) => {
        if (row.Status === 'Inactive') {
            setIsOn(false)
        } else {
            setIsOn(true)
        }
        setSelectedUser(row);
        setDialogOpen(true);
    }

    const handleDialogCancel = () => {
        setDialogOpen(false);
        setSelectedUser(null); // Clear selected row
    }


    const handleDialogConfirm = async () => {
        if (!selectedUser) return;
        try {
            // Make API call to update the status
            const updatedUser = userDatalist.find((user) => user.userId === selectedUser.userId);
            if (updatedUser) {
                // Step 2: Update the fields on the filtered record
                const updatedUserData = {
                    ...updatedUser, // Keep the original user data
                    Password: '',
                    status: selectedUser.Status === 'Active' ? 0 : 1,
                    updatedId: userId, // You can also update any additional fields like updatedId
                };

                let response: ApiResponse<any> = await execute('put', '/User/UpdateUser', updatedUserData);

                if (response!) {
                    if (response.error) {
                        // If there's an error in the response, display the error message
                        setToast({ open: true, message: response.error, type: "error" });
                    } else {
                        // If the response is successful, display the success message
                        setToast({ open: true, message: response.data, type: "success" });
                    }
                }
            }
            fetchUsers();
            setSelectedUser(null); // Clear selected row
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setDialogOpen(false);
        }
    };

  const handleContentChange = (state: boolean) => {
    setHasUnsavedChanges(state);
  };
    return (
        <AppLayout>
            <FilterCard {...props} handleModalOrNavigate={handleOpenModal} handleSearch={handleSearchGridData} ></FilterCard>
            <CustomAddUserModal handleContentChange={handleContentChange} open={modalOpen} onClose={handleCloseModal} onSubmit={handleFormSubmit} userData={selectedUser} />
            {userDatalist.length > 0 && (<CustomDataTable rows={filteredData} actionText="User" sortOptions={sortOptions} onRowEdit={handleEdit} onToggleClick={handleToggleState} isDialogOpen={dialogOpen} isToggleOn={isOn} handleCancel={handleDialogCancel} handleConfirm={handleDialogConfirm} noResultMessage={validationMessages.common_RecordNotFound("users")} ></CustomDataTable>)}
            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
            <CustomBackdrop open={loading} />
        </AppLayout>
    );
}

export default Users;